import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import './Mercado.css';

// ── Helpers de restricción → filtro ─────────────────────────────────────────

const SUDAMERICANOS = new Set([
  'Argentina','Brasil','Uruguay','Colombia','Ecuador','Venezuela',
  'Paraguay','Chile','Perú','Bolivia',
]);
const EUROPEOS = new Set([
  'España','Alemania','Francia','Portugal','Países Bajos','Bélgica',
  'Croacia','Austria','Suiza','Escocia','Inglaterra','Noruega','Suecia',
  'República Checa','Bosnia','Turquía',
]);
const AFRICANOS = new Set([
  'Marruecos','Senegal','Egipto','Ghana','Costa de Marfil','Túnez',
  'Argelia','Sudáfrica','Cabo Verde','RD Congo',
]);
const ASIATICOS = new Set([
  'Japón','Corea del Sur','Irán','Arabia Saudita','Qatar','Uzbekistán','Jordania','Irak',
]);
const CONCACAF = new Set([
  'México','USA','Canadá','Panamá','Haití','Curazao',
]);
const OCEANIA = new Set([
  'Australia','Nueva Zelanda',
]);

const calcularEdad = (fechaNac) => {
  if (!fechaNac) return null;
  const hoy = new Date(), nac = new Date(fechaNac);
  let edad = hoy.getFullYear() - nac.getFullYear();
  if (hoy.getMonth() < nac.getMonth() || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())) edad--;
  return edad;
};

// Sin tildes para comparación
const TRANSLITMAP = {
  'ø':'o','Ø':'o','ö':'o','Ö':'o','ó':'o','ò':'o','ô':'o','õ':'o','ő':'o',
  'ä':'a','Ä':'a','á':'a','à':'a','â':'a','ã':'a','å':'a','Å':'a','ā':'a','ą':'a',
  'ü':'u','Ü':'u','ú':'u','ù':'u','û':'u','ů':'u','ű':'u','ū':'u',
  'é':'e','è':'e','ê':'e','ë':'e','ě':'e','ę':'e','ē':'e',
  'í':'i','ì':'i','î':'i','ï':'i','ı':'i','ī':'i','į':'i',
  'ñ':'n','ń':'n','ň':'n',
  'ç':'c','č':'c','ć':'c',
  'ß':'ss','ś':'s','š':'s','ş':'s',
  'ž':'z','ź':'z','ż':'z',
  'ř':'r',
  'ľ':'l','ł':'l',
  'ď':'d','đ':'d','Đ':'d',
  'ť':'t','þ':'th',
  'ý':'y',
  'ğ':'g',
  'æ':'ae','Æ':'ae',
  // Georgiano → transliteración básica
  'ა':'a','ბ':'b','გ':'g','დ':'d','ე':'e','ვ':'v','ზ':'z','თ':'t',
  'ი':'i','კ':'k','ლ':'l','მ':'m','ნ':'n','ო':'o','პ':'p','ჟ':'zh',
  'რ':'r','ს':'s','ტ':'t','უ':'u','ფ':'f','ქ':'k','ღ':'gh','ყ':'q',
  'შ':'sh','ჩ':'ch','ც':'ts','ძ':'dz','წ':'ts','ჭ':'ch','ხ':'kh',
  'ჯ':'j','ჰ':'h',
  // Árabe → skip (reemplaza con espacio para no bloquear búsqueda)
  'ا':'a','ب':'b','ت':'t','ث':'th','ج':'j','ح':'h','خ':'kh','د':'d',
  'ذ':'dh','ر':'r','ز':'z','س':'s','ش':'sh','ص':'s','ض':'d','ط':'t',
  'ظ':'z','ع':'a','غ':'gh','ف':'f','ق':'q','ك':'k','ل':'l','م':'m',
  'ن':'n','ه':'h','و':'w','ي':'y','ة':'a','ى':'a',
  // Japonés/Coreano → dejar pasar (búsqueda exacta)
};

const norm = (s) => {
  if (!s) return '';
  return s.toLowerCase()
    .split('')
    .map(c => TRANSLITMAP[c] ?? c)
    .join('')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ''); // elimina cualquier carácter raro restante
};

const restriccionToFiltro = (texto) => {
  const t = norm(texto);

  // Zurdos
  if (t.includes('zurdo'))
    return { label: 'Zurdos', fn: j => j.pie_habil === 'Izquierdo' };

  // Sub-23
  if (t.includes('sub-23') || t.includes('sub23'))
    return { label: 'Sub-23', fn: j => { const e = calcularEdad(j.fecha_nacimiento); return e !== null && e <= 23; } };

  // +33 años
  if (t.includes('+33') || (t.includes('33') && t.includes('ano')))
    return { label: '+33 años', fn: j => { const e = calcularEdad(j.fecha_nacimiento); return e !== null && e >= 33; } };

  // Máx $6.5M
  if (t.includes('6.5') || t.includes('6,5'))
    return { label: 'Máx $6.5M', fn: j => j.precio <= 6_500_000 };

  // Última final del mundial
  if (t.includes('ultima final') || t.includes('ltima final'))
    return { label: 'Final mundial (ARG+FRA)', fn: j => ['Argentina','Francia'].includes(j.nacionalidad) };

  // Ex ligas
  if (t.includes('ex serie a'))
    return { label: 'Ex Serie A', fn: j => j.ex_ligas?.includes('Serie A') };
  if (t.includes('ex premier'))
    return { label: 'Ex Premier League', fn: j => j.ex_ligas?.includes('Premier League') };
  if (t.includes('ex la liga'))
    return { label: 'Ex La Liga', fn: j => j.ex_ligas?.includes('La Liga') };
  if (t.includes('ex ligue'))
    return { label: 'Ex Ligue 1', fn: j => j.ex_ligas?.includes('Ligue 1') };
  if (t.includes('ex bundesliga'))
    return { label: 'Ex Bundesliga', fn: j => j.ex_ligas?.includes('Bundesliga') };
  if (t.includes('ex eredivisie'))
    return { label: 'Ex Eredivisie', fn: j => j.ex_ligas?.includes('Eredivisie') };

  // Ligas actuales
  if (t.includes('serie a') && !t.includes('ex'))
    return { label: 'Serie A actual', fn: j => j.liga_club === 'Serie A' };
  if (t.includes('premier') && !t.includes('ex'))
    return { label: 'Premier actual', fn: j => j.liga_club === 'Premier League' };
  if (t.includes('la liga') && !t.includes('ex'))
    return { label: 'La Liga actual', fn: j => j.liga_club === 'La Liga' };
  if (t.includes('ligue') && !t.includes('ex'))
    return { label: 'Ligue 1 actual', fn: j => j.liga_club === 'Ligue 1' };
  if (t.includes('bundesliga') && !t.includes('ex'))
    return { label: 'Bundesliga actual', fn: j => j.liga_club === 'Bundesliga' };
  if (t.includes('eredivisie') && !t.includes('ex'))
    return { label: 'Eredivisie actual', fn: j => j.liga_club === 'Eredivisie' };

  // Posición + continente (antes que solo-continente)
  if (t.includes('defensor') && t.includes('sudamericano'))
    return { label: 'Def. Sudamericano', fn: j => j.posicion?.sigla === 'DEF' && SUDAMERICANOS.has(j.nacionalidad) };
  if (t.includes('defensor') && t.includes('europeo'))
    return { label: 'Def. Europeo', fn: j => j.posicion?.sigla === 'DEF' && EUROPEOS.has(j.nacionalidad) };
  if (t.includes('defensor') && t.includes('africano'))
    return { label: 'Def. Africano', fn: j => j.posicion?.sigla === 'DEF' && AFRICANOS.has(j.nacionalidad) };
  if (t.includes('defensor') && t.includes('asiatico'))
    return { label: 'Def. Asiático', fn: j => j.posicion?.sigla === 'DEF' && ASIATICOS.has(j.nacionalidad) };
  if (t.includes('defensor') && t.includes('oceania'))
    return { label: 'Def. Oceanía', fn: j => j.posicion?.sigla === 'DEF' && OCEANIA.has(j.nacionalidad) };
  if (t.includes('delantero') && t.includes('asiatico'))
    return { label: 'Del. Asiático', fn: j => j.posicion?.sigla === 'DEL' && ASIATICOS.has(j.nacionalidad) };
  if (t.includes('delantero') && t.includes('africano'))
    return { label: 'Del. Africano', fn: j => j.posicion?.sigla === 'DEL' && AFRICANOS.has(j.nacionalidad) };

  // Campeonas del mundo
  if (t.includes('campeonas') || t.includes('selecciones campeonas'))
    return { label: 'Camp. del mundo', fn: j => new Set(['Argentina','Brasil','Uruguay','España','Francia','Inglaterra','Italia','Alemania']).has(j.nacionalidad) };

  // Europeos
  if (t.includes('europeo') && !t.includes('defensor') && !t.includes('delantero'))
    return { label: 'Europeos', fn: j => EUROPEOS.has(j.nacionalidad) };

  // América (CONCACAF + Sudamérica)
  if (t.includes('de america') || t.includes('de am'))
    return { label: 'Americanos', fn: j => SUDAMERICANOS.has(j.nacionalidad) || CONCACAF.has(j.nacionalidad) };

  // Misma selección (no filtrable en mercado)
  if (t.includes('misma selecci'))
    return null;

  // Ranking FIFA
  if (t.includes('3 mejores') || t.includes('mejores selecciones'))
    return { label: 'Sin Top 3 FIFA', fn: j => !['Francia','España','Argentina'].includes(j.nacionalidad) };
  if (t.includes('3 peores') || t.includes('peores selecciones'))
    return { label: 'Peores 3 FIFA', fn: j => ['Nueva Zelanda','Haiti','Curazao'].includes(j.nacionalidad) };

  // Dorsales
  //if (t.includes('dorsal') && t.includes('10'))
    //return { label: 'Dorsales #10', fn: j => Number(j.dorsal) === 10 };
  //if (t.includes('dorsal') && t.includes(' 9') && !t.includes('11'))
    //return { label: 'Dorsales #9', fn: j => Number(j.dorsal) === 9 };
 // if (t.includes('dorsal') && t.includes(' 7'))
    //return { label: 'Dorsales #7', fn: j => Number(j.dorsal) === 7 };
 // if (t.includes('dorsal') && t.includes('11'))
    //return { label: 'Dorsales #11', fn: j => Number(j.dorsal) === 11 };

  return null;
};


// ── Componente ───────────────────────────────────────────────────────────────
const Mercado = ({
  posicionInicial,
  onClose,
  onSeleccionarJugador,
  equipoActual,
  restriccionesActualizadas = [],
  modoLibre = false,
}) => {
  const [jugadores, setJugadores]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [busqueda, setBusqueda]       = useState('');
  const [filtroActivo, setFiltroActivo] = useState(posicionInicial);
  const [toast, setToast]             = useState(null);
  const [sortKey, setSortKey]           = useState('apellido'); // 'apellido' | 'precio' | 'promedio'
  const [sortDir, setSortDir]           = useState('asc'); // 'asc' | 'desc'
  const [promedios, setPromedios]       = useState({}); // { jugador_id: promedio_pts }

  const mostrarToast = (msg, tipo = 'error') => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3000);
  };

  // Filtros avanzados
  const [precioMin, setPrecioMin]       = useState('');
  const [precioMax, setPrecioMax]       = useState('');
  const [filtroNac, setFiltroNac]       = useState('');
  const [filtroLiga, setFiltroLiga]     = useState('');
  const [filtroRestr, setFiltroRestr]   = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [nacDropdownAbierto, setNacDropdownAbierto] = useState(false);

  useEffect(() => { setFiltroActivo(posicionInicial); }, [posicionInicial]);

  useEffect(() => {
    const traerJugadores = async () => {
      setLoading(true);
      try {
        // Leer contexto activo (liga | mundial) desde sistema_config
        const { data: config } = await supabase
          .from('sistema_config')
          .select('valor')
          .eq('clave', 'mercado_contexto')
          .single();

        const contexto = config?.valor || 'liga';

        let query = supabase.from('jugadores').select(`
          id, nombre, apellido, precio, pie_habil, nacionalidad,
          fecha_nacimiento, altura, status, dorsal, seleccion, liga_club, contexto, ex_ligas,
          posicion:posiciones(sigla),
          club:clubes(sigla, nombre)
        `).eq('contexto', contexto);

        if (filtroActivo) {
          const { data: posData } = await supabase
            .from('posiciones').select('id').eq('sigla', filtroActivo).single();
          if (posData) query = query.eq('posicion_id', posData.id);
        }
        const { data } = await query.order('apellido', { ascending: true });
        setJugadores(data || []);

        // Calcular promedio desde jugador_puntos_fecha (puntos base sin capitán duplicado)
        // Dividir por cantidad de fechas con datos cargados
        const { data: jpfData } = await supabase
          .from('jugador_puntos_fecha')
          .select('jugador_id, puntos_total, jornada');
        const sumaMap = {}, jornadasConDatos = new Set();
        for (const row of (jpfData || [])) {
          if (row.puntos_total == null) continue;
          sumaMap[row.jugador_id] = (sumaMap[row.jugador_id] || 0) + Number(row.puntos_total);
          jornadasConDatos.add(row.jornada);
        }
        const cantFechas = jornadasConDatos.size || 1;
        const prom = {};
        for (const jid of Object.keys(sumaMap)) {
          prom[jid] = sumaMap[jid] / cantFechas;
        }
        setPromedios(prom);

      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    traerJugadores();
  }, [filtroActivo]);

  // Chips de restricciones — siempre visibles hasta confirmar (cumplida o no)
  const chipsRestricciones = useMemo(() =>
    restriccionesActualizadas
      .filter(r => !r.anulada)
      .map(r => ({ ...restriccionToFiltro(r.texto), cumplida: r.cumplida }))
      .filter(Boolean),
  [restriccionesActualizadas]);



  // Filtrado client-side
  const filtrados = useMemo(() => {
    const minP = precioMin !== '' ? parseFloat(precioMin) * 1_000_000 : null;
    const maxP = precioMax !== '' ? parseFloat(precioMax) * 1_000_000 : null;
    const q = norm(busqueda);
    const filtered = jugadores.filter(j => {
      if (q && !norm(`${j.nombre} ${j.apellido}`).includes(q)) return false;
      if (filtroNac && j.nacionalidad !== filtroNac) return false;
      if (filtroLiga && j.liga_club !== filtroLiga) return false;
      if (minP !== null && j.precio < minP) return false;
      if (maxP !== null && j.precio > maxP) return false;
      if (filtroRestr?.fn && !filtroRestr.fn(j)) return false;
      return true;
    });
    if (!filtered) return [];
    return [...filtered].sort((a, b) => {
      if (sortKey === 'precio') return sortDir === 'asc' ? a.precio - b.precio : b.precio - a.precio;
      if (sortKey === 'promedio') {
        const pa = promedios[a.id] ?? -1;
        const pb = promedios[b.id] ?? -1;
        return sortDir === 'asc' ? pa - pb : pb - pa;
      }
      const na = `${a.apellido} ${a.nombre}`.toLowerCase();
      const nb = `${b.apellido} ${b.nombre}`.toLowerCase();
      return sortDir === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
    });
  }, [jugadores, busqueda, filtroNac, filtroLiga, precioMin, precioMax, filtroRestr, sortKey, sortDir, promedios]);

  const handleSeleccionar = (jugador) => {
    const siglaJugador = jugador.posicion?.sigla || jugador.posicion;
    if (!modoLibre && posicionInicial !== null) {
      if (!posicionInicial) { mostrarToast('Error: No se definió una posición para este slot.'); return; }
      if (siglaJugador !== posicionInicial) {
        mostrarToast(`${jugador.apellido || jugador.nombre} es ${siglaJugador}, no puede ir en ${posicionInicial}`);
        return;
      }
    }
    if (Object.values(equipoActual || {}).some(j => j?.id === jugador.id)) {
      mostrarToast(`${jugador.apellido || jugador.nombre} ya está en tu formación`);
      return;
    }
    onSeleccionarJugador(jugador);
  };

  const hayFiltrosActivos = filtroNac || filtroLiga || precioMin || precioMax || filtroRestr;
  const limpiarFiltros = () => { setPrecioMin(''); setPrecioMax(''); setFiltroNac(''); setFiltroLiga(''); setFiltroRestr(null); };

  return (
    <div className="mercado-panel">
      <div className="mercado-header">

        {/* Título + cerrar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <h3 style={{ color: '#00f2ff', margin: 0 }}>
            MERCADO{filtroActivo ? `: ${filtroActivo}` : ''}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '20px' }}>×</button>
        </div>

        {/* Botones de posición */}
        <div className="selector-posiciones">
          {['ARQ', 'DEF', 'VOL', 'DEL'].map(pos => (
            <button key={pos}
              className={`btn-filtro ${filtroActivo === pos ? 'activo' : ''}`}
              onClick={() => setFiltroActivo(filtroActivo === pos ? null : pos)}
            >{pos}</button>
          ))}
        </div>

        {/* Ordenar por puntos promedio */}
        <button
          onClick={() => {
            if (sortKey === 'promedio') setSortDir(d => d === 'asc' ? 'desc' : 'asc');
            else { setSortKey('promedio'); setSortDir('desc'); }
          }}
          style={{ width: '100%', marginBottom: '8px', padding: '7px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 'bold', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: '0.2s',
            background: sortKey === 'promedio' ? '#f59e0b22' : 'transparent',
            border: `1px solid ${sortKey === 'promedio' ? '#f59e0b' : '#30363d'}`,
            color: sortKey === 'promedio' ? '#f59e0b' : '#475569',
          }}>
          <span>Ordenar por puntos promedio</span>
          <span style={{ fontSize: '0.65rem' }}>{sortKey === 'promedio' ? (sortDir === 'desc' ? '▼ mayor' : '▲ menor') : '⇅'}</span>
        </button>

        {/* Buscador */}
        <input
          className="mercado-search"
          placeholder="Buscar jugador..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{ width: '100%', padding: '8px', marginBottom: '8px', borderRadius: '4px', border: '1px solid #30363d', background: '#0a0f19', color: '#fff' }}
        />

        {/* Chips de restricciones — siempre visibles hasta confirmar */}
        {chipsRestricciones.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <p style={{ color: '#64748b', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 5px' }}>
              Filtrar por restricción
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {chipsRestricciones.map((chip, i) => {
                const activo = filtroRestr?.label === chip.label;
                const cumplida = chip.cumplida;
                return (
                  <button key={i} onClick={() => setFiltroRestr(activo ? null : chip)}
                    style={{
                      padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem',
                      cursor: 'pointer', transition: '0.2s', fontWeight: 'bold',
                      background: activo ? 'rgba(0,242,255,0.15)' : cumplida ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${activo ? '#00f2ff' : cumplida ? '#22c55e55' : '#334155'}`,
                      color: activo ? '#00f2ff' : cumplida ? '#22c55e' : '#94a3b8',
                    }}
                  >{cumplida ? '✓ ' : ''}{chip.label}</button>
                );
              })}
            </div>
          </div>
        )}

        {/* Toggle filtros avanzados */}
        <button
          onClick={() => setMostrarFiltros(v => !v)}
          style={{
            width: '100%', padding: '5px 8px',
            background: 'transparent',
            border: `1px solid ${hayFiltrosActivos ? '#f59e0b' : '#1e293b'}`,
            borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem',
            color: hayFiltrosActivos ? '#f59e0b' : '#475569',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            transition: '0.2s',
          }}
        >
          <span>⚙ Filtros {hayFiltrosActivos ? '(activos)' : 'avanzados'}</span>
          <span style={{ fontSize: '0.6rem' }}>{mostrarFiltros ? '▲' : '▼'}</span>
        </button>

        {/* Panel de filtros avanzados */}
        {mostrarFiltros && (
          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: '#0a0f19', borderRadius: '8px', border: '1px solid #1e293b' }}>

            {/* Precio */}
            <div>
              <p style={{ color: '#64748b', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 5px' }}>
                Precio (en millones)
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <input type="number" placeholder="Mín ($M)" value={precioMin}
                  onChange={e => setPrecioMin(e.target.value)}
                  style={{ width: '100%', padding: '5px 8px', background: '#111827', border: '1px solid #1e293b', borderRadius: '5px', color: '#fff', fontSize: '0.82rem', boxSizing: 'border-box' }}
                />
                <input type="number" placeholder="Máx ($M)" value={precioMax}
                  onChange={e => setPrecioMax(e.target.value)}
                  style={{ width: '100%', padding: '5px 8px', background: '#111827', border: '1px solid #1e293b', borderRadius: '5px', color: '#fff', fontSize: '0.82rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Nacionalidad — dropdown custom con banderas */}
            {(() => {
              const PAISES = [
                { grupo: 'Sudamérica', items: [
                  { val: 'Argentina', flag: 'ar' }, { val: 'Brasil', flag: 'br' },
                  { val: 'Colombia', flag: 'co' }, { val: 'Uruguay', flag: 'uy' },
                  { val: 'Paraguay', flag: 'py' }, { val: 'Ecuador', flag: 'ec' },
                ]},
                { grupo: 'Europa', items: [
                  { val: 'España', flag: 'es' }, { val: 'Francia', flag: 'fr' },
                  { val: 'Alemania', flag: 'de' }, { val: 'Inglaterra', flag: 'gb-eng' },
                  { val: 'Portugal', flag: 'pt' }, { val: 'Países Bajos', flag: 'nl' },
                  { val: 'Bélgica', flag: 'be' }, { val: 'Croacia', flag: 'hr' },
                  { val: 'Noruega', flag: 'no' }, { val: 'Suecia', flag: 'se' },
                  { val: 'Suiza', flag: 'ch' }, { val: 'Austria', flag: 'at' },
                  { val: 'Escocia', flag: 'gb-sct' }, { val: 'República Checa', flag: 'cz' },
                  { val: 'Bosnia', flag: 'ba' }, { val: 'Turquía', flag: 'tr' },
                ]},
                { grupo: 'África', items: [
                  { val: 'Marruecos', flag: 'ma' }, { val: 'Senegal', flag: 'sn' },
                  { val: 'Ghana', flag: 'gh' }, { val: 'Egipto', flag: 'eg' },
                  { val: 'Costa de Marfil', flag: 'ci' }, { val: 'Túnez', flag: 'tn' },
                  { val: 'Argelia', flag: 'dz' }, { val: 'Sudáfrica', flag: 'za' },
                  { val: 'Cabo Verde', flag: 'cv' }, { val: 'RD Congo', flag: 'cd' },
                ]},
                { grupo: 'Asia / Oceanía', items: [
                  { val: 'Japón', flag: 'jp' }, { val: 'Corea del Sur', flag: 'kr' },
                  { val: 'Irán', flag: 'ir' }, { val: 'Arabia Saudita', flag: 'sa' },
                  { val: 'Qatar', flag: 'qa' }, { val: 'Uzbekistán', flag: 'uz' },
                  { val: 'Jordania', flag: 'jo' }, { val: 'Irak', flag: 'iq' },
                  { val: 'Australia', flag: 'au' }, { val: 'Nueva Zelanda', flag: 'nz' },
                ]},
                { grupo: 'CONCACAF', items: [
                  { val: 'México', flag: 'mx' }, { val: 'USA', flag: 'us' },
                  { val: 'Canadá', flag: 'ca' }, { val: 'Panamá', flag: 'pa' },
                  { val: 'Haití', flag: 'ht' }, { val: 'Curazao', flag: 'cw' },
                ]},
              ];
              const seleccionado = PAISES.flatMap(g => g.items).find(p => p.val === filtroNac);
              const [abierto, setAbierto] = [nacDropdownAbierto, setNacDropdownAbierto];
              return (
                <div style={{ position: 'relative' }}>
                  <p style={{ color: '#64748b', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 5px' }}>Nacionalidad</p>
                  <button onClick={() => setAbierto(a => !a)} style={{
                    width: '100%', padding: '6px 10px', background: '#111827',
                    border: `1px solid ${filtroNac ? '#00f2ff' : '#1e293b'}`, borderRadius: '6px',
                    color: filtroNac ? '#fff' : '#475569', fontSize: '0.82rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {seleccionado
                        ? <img src={`https://flagcdn.com/20x15/${seleccionado.flag}.png`} alt={seleccionado.val} style={{ width: '20px', height: '15px', objectFit: 'cover', borderRadius: '2px' }} />
                        : <span></span>
                      }
                      <span>{seleccionado ? seleccionado.val : 'Todas las nacionalidades'}</span>
                    </div>
                    <span style={{ fontSize: '0.6rem', color: '#475569' }}>{abierto ? '▲' : '▼'}</span>
                  </button>
                  {abierto && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#0f172a', border: '1px solid #1e293b', borderRadius: '6px', zIndex: 9999, maxHeight: '240px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                      <div onClick={() => { setFiltroNac(''); setAbierto(false); }}
                        style={{ padding: '7px 12px', cursor: 'pointer', color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid #1e293b' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#1e293b'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        Todas las nacionalidades
                      </div>
                      {PAISES.map(grupo => (
                        <div key={grupo.grupo}>
                          <div style={{ padding: '5px 12px', color: '#334155', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1px', background: '#080d14' }}>
                            {grupo.grupo}
                          </div>
                          {grupo.items.map(p => (
                            <div key={p.val} onClick={() => { setFiltroNac(p.val); setAbierto(false); }}
                              style={{ padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem', color: filtroNac === p.val ? '#00f2ff' : '#e2e8f0', background: filtroNac === p.val ? '#00f2ff11' : 'transparent', display: 'flex', alignItems: 'center', gap: '10px' }}
                              onMouseEnter={e => { if (filtroNac !== p.val) e.currentTarget.style.background = '#1e293b'; }}
                              onMouseLeave={e => { if (filtroNac !== p.val) e.currentTarget.style.background = 'transparent'; }}>
                              <img src={`https://flagcdn.com/20x15/${p.flag}.png`} alt={p.val} style={{ width: '20px', height: '15px', objectFit: 'cover', borderRadius: '2px', flexShrink: 0 }} />
                              {p.val}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Liga — comentado, el filtro por liga se hace via restricciones */}
            {/* <div>
              <p style={{ color: '#64748b', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 5px' }}>Liga</p>
              <select value={filtroLiga} onChange={e => setFiltroLiga(e.target.value)}
                style={{ width: '100%', padding: '5px 8px', background: '#111827', border: `1px solid ${filtroLiga ? '#00f2ff' : '#1e293b'}`, borderRadius: '5px', color: filtroLiga ? '#fff' : '#475569', fontSize: '0.82rem', cursor: 'pointer' }}>
                <option value="">Todas las ligas</option>
                <option value="Liga Profesional">🇦🇷 Liga Profesional</option>
                <option value="Premier League">🏴󠁧󠁢󠁥󠁮󠁧󠁿 Premier League</option>
                <option value="La Liga">🇪🇸 La Liga</option>
                <option value="Serie A">🇮🇹 Serie A</option>
                <option value="Bundesliga">🇩🇪 Bundesliga</option>
                <option value="Ligue 1">🇫🇷 Ligue 1</option>
                <option value="Eredivisie">🇳🇱 Eredivisie</option>
                <option value="Primeira Liga">🇵🇹 Primeira Liga</option>
                <option value="Liga Arabia Saudita">🇸🇦 Liga Arabia Saudita</option>
                <option value="MLS">🇺🇸 MLS</option>
              </select>
            </div> */}

            {/* Limpiar */}
            {hayFiltrosActivos && (
              <button onClick={limpiarFiltros}
                style={{ padding: '5px', background: 'transparent', border: '1px solid #ef4444', borderRadius: '5px', color: '#ef4444', fontSize: '0.75rem', cursor: 'pointer', transition: '0.2s' }}
              >
                ✕ Limpiar todos los filtros
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lista */}
      <div className="lista-jugadores-scroll">
        {loading ? (
          <p style={{ color: '#475569', padding: '20px', textAlign: 'center' }}>Cargando...</p>
        ) : (filtrados || []).length === 0 ? (
          <p style={{ color: '#334155', padding: '20px', textAlign: 'center', fontSize: '0.85rem' }}>
            Sin resultados{hayFiltrosActivos ? ' — ajustá los filtros' : ''}
          </p>
        ) : <>
          {/* Header con flechas de ordenamiento */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #1e293b', background: '#080d14', position: 'sticky', top: 0 }}>
            <button onClick={() => { if (sortKey === 'apellido') setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey('apellido'); setSortDir('asc'); } }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: sortKey === 'apellido' ? '#00f2ff' : '#475569', fontSize: '0.72rem', fontWeight: 'bold', padding: 0 }}>
              JUGADOR
              <span style={{ fontSize: '0.65rem' }}>
                {sortKey === 'apellido' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
              </span>
            </button>
            <button onClick={() => { if (sortKey === 'precio') setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey('precio'); setSortDir('desc'); } }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: sortKey === 'precio' ? '#00f2ff' : '#475569', fontSize: '0.72rem', fontWeight: 'bold', padding: 0 }}>
              PRECIO
              <span style={{ fontSize: '0.65rem' }}>
                {sortKey === 'precio' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
              </span>
            </button>

          </div>
          {(filtrados || []).map(j => {
          const STATUS_COLOR = { titular: '#22c55e', duda: '#f97316', lesionado: '#ef4444', no_juega: '#ef4444' };
          const dotColor = STATUS_COLOR[j.status] || '#22c55e';
          return (
            <div key={j.id} className="item-jugador-mercado" onClick={() => handleSeleccionar(j)}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{
                  width: '7px', height: '7px', borderRadius: '50%',
                  background: dotColor, boxShadow: `0 0 5px ${dotColor}cc`,
                  flexShrink: 0, marginTop: '9px',
                }} />
                <div>
                  <div className="name">{j.apellido ? `${j.apellido}, ${j.nombre}` : j.nombre}</div>
                  <div className="meta">
                    {j.posicion?.sigla || 'N/A'} • {j.club?.sigla || 'S/C'} • {j.nacionalidad || 'N/A'} • {j.pie_habil === 'Izquierdo' ? 'IZQ' : 'DER'}{j.dorsal ? ` • #${j.dorsal}` : ''}{promedios[j.id] != null ? <span style={{color:'#f59e0b',marginLeft:'4px'}}>• {promedios[j.id] % 1 === 0 ? promedios[j.id] : promedios[j.id].toFixed(1)}pts</span> : ''}
                  </div>
                </div>
              </div>
              <div className="price">${(j.precio / 1_000_000).toFixed(1)}M</div>
            </div>
          );
        })}
        </>
        }
      </div>

      {/* ⚠️ NO BORRAR: Toast de notificaciones */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
          background: toast.tipo === 'error' ? '#1a0808' : '#081a08',
          border: `1px solid ${toast.tipo === 'error' ? '#ef444466' : '#22c55e66'}`,
          color: toast.tipo === 'error' ? '#ef4444' : '#22c55e',
          padding: '12px 24px', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 'bold',
          zIndex: 99999, boxShadow: '0 4px 24px rgba(0,0,0,0.6)', whiteSpace: 'nowrap',
        }}>
          ⚠️ {toast.msg}
        </div>
      )}
    </div>
  );
};

export default Mercado;